/**
 * WelcomeCallPrep
 *
 * Right panel component showing auto-populated call prep notes.
 * Items are added from case context and documents to collect.
 * User can also add custom notes.
 *
 * When canContactClient is false, renders as "Follow Up with Attorney" instead.
 */
import {
  Briefcase,
  Check,
  ClipboardCopy,
  FileText,
  MessageSquare,
  Phone,
  Trash2,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import type { CallPrepItem, CallPrepSource } from '../types';

interface WelcomeCallPrepProps {
  items: CallPrepItem[];
  onRemoveItem: (id: string) => void;
  customNote: string;
  onCustomNoteChange: (note: string) => void;
  getFormattedText: () => string;
  /** Whether client can be contacted directly. If false, shows "Follow Up with Attorney" mode. */
  canContactClient: boolean;
}

const getSourceIcon = (source: CallPrepSource) => {
  switch (source) {
    case 'context':
      return <MessageSquare className="h-3.5 w-3.5" />;
    case 'document':
      return <FileText className="h-3.5 w-3.5" />;
    default:
      return null;
  }
};

const getSourceColor = (source: CallPrepSource) => {
  switch (source) {
    case 'context':
      return 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800';
    case 'document':
      return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800';
    default:
      return 'bg-zinc-50 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700';
  }
};

interface CallPrepItemRowProps {
  item: CallPrepItem;
  onRemove: () => void;
}

const CallPrepItemRow = ({ item, onRemove }: CallPrepItemRowProps) => {
  return (
    <div
      className={cn(
        'group flex items-start gap-2 rounded-md border px-2.5 py-2 transition-colors',
        getSourceColor(item.source),
      )}
    >
      <div className="mt-0.5 flex-shrink-0">{getSourceIcon(item.source)}</div>
      <p className="min-w-0 flex-1 text-xs leading-relaxed text-zinc-700 dark:text-zinc-300">
        {item.text}
      </p>
      <button
        type="button"
        onClick={onRemove}
        className="flex-shrink-0 rounded-md p-1 text-zinc-400 dark:text-zinc-500 opacity-0 transition-all hover:bg-white dark:hover:bg-zinc-700 hover:text-zinc-600 dark:hover:text-zinc-300 group-hover:opacity-100"
        aria-label="Remove item"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
};

export const WelcomeCallPrep = ({
  items,
  onRemoveItem,
  customNote,
  onCustomNoteChange,
  getFormattedText,
  canContactClient,
}: WelcomeCallPrepProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopyToClipboard = useCallback(() => {
    const text = getFormattedText();
    if (!text.trim()) {
      toast.error('No content to copy');
      return;
    }

    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        toast.success('Copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        toast.error('Failed to copy to clipboard');
      });
  }, [getFormattedText]);

  // Group items by source
  const contextItems = items.filter((item) => item.source === 'context');
  const documentItems = items.filter((item) => item.source === 'document');

  const isEmpty = items.length === 0 && !customNote.trim();

  // Determine title and icon based on client contact setting
  const title = canContactClient ? 'Call Prep' : 'Attorney Follow Up';
  const HeaderIcon = canContactClient ? Phone : Briefcase;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-3">
        <div className="flex items-center gap-2">
          <HeaderIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
            {title}
          </h3>
          {items.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-indigo-100 dark:bg-indigo-900/30 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-400">
              {items.length}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopyToClipboard}
          disabled={isEmpty}
          className="h-7 gap-1.5 px-2 text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Copied</span>
            </>
          ) : (
            <>
              <ClipboardCopy className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Copy</span>
            </>
          )}
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {/* Discussion Points (Context) */}
          {contextItems.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wide">
                <MessageSquare className="h-3.5 w-3.5" />
                Discussion
              </div>
              <div className="space-y-2">
                {contextItems.map((item) => (
                  <CallPrepItemRow
                    key={item.id}
                    item={item}
                    onRemove={() => onRemoveItem(item.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Documents to Collect */}
          {documentItems.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wide">
                <FileText className="h-3.5 w-3.5" />
                Collect
              </div>
              <div className="space-y-2">
                {documentItems.map((item) => (
                  <CallPrepItemRow
                    key={item.id}
                    item={item}
                    onRemove={() => onRemoveItem(item.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Custom Notes - always visible */}
          <div className="space-y-2">
            <div className="text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wide">
              Notes
            </div>
            <Textarea
              value={customNote}
              onChange={(e) => onCustomNoteChange(e.target.value)}
              placeholder="Add custom call prep notes..."
              className="min-h-[100px] resize-none text-xs border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 focus:border-indigo-500 dark:focus:border-indigo-400"
            />
          </div>
        </div>
      </div>

      {/* Footer hint */}
      <div className="border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50 px-4 py-3">
        <p className="text-xs text-zinc-600 dark:text-zinc-400">
          Your notes will be saved to the case when you complete this review
        </p>
      </div>
    </div>
  );
};
